import mod from "/web_modules/mime-to-jmap.js";
const parseOpts = {
  want_headers: ["header:X-Gmail-Labels:asText"]
};
let nextThreadId = 1000;
class ThreadGroups {
  constructor() {
    this.threadIdForMsgId = new Map();
    this.msgIdsForThread = new Map();
    this.idForMsgId = new Map();
    this.mailboxesForId = new Map();
  }
  add(id, json, mailboxes) {
    const msgId = json.messageId && json.messageId.length ? json.messageId[0] : id;
    const refs = [msgId];
    if (json.inReplyTo)
      refs.push(...json.inReplyTo);
    if (json.references)
      refs.push(...json.references);
    this.idForMsgId.set(msgId, id);
    this.mailboxesForId.set(id, mailboxes);
    let candidates = new Set();
    let threadId;
    for (const m of refs) {
      const tid = this.threadIdForMsgId.get(m);
      if (tid)
        candidates.add(tid);
    }
    if (candidates.size === 0) {
      threadId = `T${nextThreadId++}`;
      this.msgIdsForThread.set(threadId, refs);
    } else if (candidates.size === 1) {
      threadId = candidates.values().next().value;
      this.msgIdsForThread.get(threadId).push(...refs);
    } else {
      threadId = candidates.values().next().value;
      const msgIds = this.msgIdsForThread.get(threadId);
      msgIds.push(...refs);
      for (const t of candidates) {
        if (t === threadId)
          continue;
        for (const m of this.msgIdsForThread.get(t)) {
          this.threadIdForMsgId.set(m, threadId);
          msgIds.push(m);
        }
        this.msgIdsForThread.delete(t);
      }
    }
    for (const m of refs) {
      this.threadIdForMsgId.set(m, threadId);
    }
  }
  finalize(emails) {
    let threads = new Map();
    for (const [threadid, msgIds] of this.msgIdsForThread) {
      const idSet = new Set();
      for (const m of msgIds) {
        const id = this.idForMsgId.get(m);
        if (id != null)
          idSet.add(id);
      }
      const messages = Array.from(idSet).sort((a, b) => {
        const ma = emails.get(a);
        const mb = emails.get(b);
        const da = new Date(ma.sentAt || ma.receivedAt);
        const db = new Date(mb.sentAt || mb.receivedAt);
        return da < db ? -1 : da == db ? 0 : 1;
      });
      const firstMessage = emails.get(messages[0]);
      let lastDate = 0;
      let hasAttachments = false;
      const participantSet = new Set();
      const mailboxSet = new Set();
      for (const mid of messages) {
        const json = emails.get(mid);
        const from = json.from[0];
        if (from)
          participantSet.add(from.name || from.email);
        if (Object.keys(json.attachments).length)
          hasAttachments = true;
        lastDate = Math.max(lastDate, +new Date(json.receivedAt || json.sentAt));
        for (const m of this.mailboxesForId.get(mid))
          mailboxSet.add(m);
      }
      const thread = {
        subject: firstMessage.subject,
        preview: firstMessage.preview || "",
        messages,
        hasAttachments,
        lastDate,
        participants: Array.from(participantSet),
        mailboxes: Array.from(mailboxSet)
      };
      threads.set(threadid, thread);
    }
    return threads;
  }
}
const yieldUI = () => new Promise((resolve) => {
  setTimeout(resolve, 0);
});
const isProbablyMbox = (buf) => {
  return buf[0] === 70 && buf[1] === 114 && buf[2] === 111 && buf[3] === 109 && buf[4] === 32;
};
async function* eachEmailIn(buf, lastModified) {
  if (isProbablyMbox(buf)) {
    console.log("Parsing as mbox file");
    for await (const {msg, progress} of mod.mbox_each_progress([buf])) {
      const {body, mboxFromAddress, receivedAt} = mod.mbox_to_eml(msg, true);
      try {
        const {json} = mod.envelope_to_jmap(body, parseOpts);
        json.receivedAt = receivedAt;
        yield {
          json,
          progress,
          body
        };
      } catch (e) {
        console.error("Could not parse email", e, body);
      }
    }
  } else {
    console.log("Parsing as single email");
    const {json} = mod.envelope_to_jmap(buf, parseOpts);
    json.receivedAt = lastModified;
    yield {
      json,
      progress: buf.length,
      body: buf
    };
  }
}
export async function scanEmails(files, setProgress) {
  await mod.ready;
  const bufs = files.map(({buf}) => new Uint8Array(buf));
  const totalLength = bufs.reduce((a, b) => a + b.byteLength, 0);
  const emailBytes = new Map();
  const emails = new Map();
  const threadGroup = new ThreadGroups();
  const messagesForThread = new Map();
  let processedLength = 0;
  for (let i = 0; i < bufs.length; i++) {
    const buf = bufs[i];
    const {name, lastModified} = files[i];
    for await (const {json, progress, body} of eachEmailIn(buf, lastModified)) {
      const id = json.id = "M" + json.blobId;
      emailBytes.set(id, body);
      const msgMailboxes = ["All"];
      const gmailLabels = json["header:X-Gmail-Labels:asText"];
      if (gmailLabels) {
        const labels = gmailLabels.split(",");
        for (const k of labels) {
          msgMailboxes.push(k);
        }
      }
      emails.set(id, json);
      threadGroup.add(id, json, msgMailboxes);
      if (emails.size % 20 === 0) {
        if (setProgress)
          setProgress("Indexing file " + name, (progress + processedLength) / totalLength);
        await yieldUI();
      }
    }
    processedLength += buf.length;
  }
  if (setProgress)
    setProgress("Threading messages", 0);
  await yieldUI();
  const threads = threadGroup.finalize(emails);
  if (setProgress)
    setProgress("Adding messages to mailboxes", 0);
  await yieldUI();
  const mailboxThreadSets = new Map();
  for (const [tid, thread] of threads) {
    if (thread.hasAttachments)
      thread.mailboxes.push("Has attachments");
    for (const m of thread.mailboxes) {
      let mailbox = mailboxThreadSets.get(m);
      if (!mailbox)
        mailboxThreadSets.set(m, mailbox = new Set());
      mailbox.add(tid);
    }
  }
  const mailboxes = {};
  for (const [mailbox, threadIds] of mailboxThreadSets) {
    mailboxes[mailbox] = Array.from(threadIds).sort((a, b) => {
      const ta = threads.get(a).lastDate;
      const tb = threads.get(b).lastDate;
      return tb - ta;
    });
  }
  return {
    emailBytes,
    threads,
    mailboxes
  };
}
